"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  Shield,
  Zap,
  CheckCircle2,
  Lock,
  BarChart3,
  FileText,
  Search,
  Scale,
  Server,
  ArrowRight,
  XCircle,
  Loader2,
  Sparkles,
  Upload
} from "lucide-react";
import { toast } from "sonner";
import { PLANS } from "./config/plans"; // Mantendo sua importação original
import { Input } from "@/components/ui/input";
import { ValidusLogo } from "@/components/ValidusLogo";

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [fileAnalyze, setFileAnalyze] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCaptureLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Por favor, digite seu e-mail.");
      return;
    }
    if (!fileAnalyze) {
      toast.error("Por favor, selecione um edital (PDF).");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("whatsapp", whatsapp);
      formData.append("file", fileAnalyze);

      const res = await fetch('/api/leads', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Edital recebido! Nossos agentes estão processando e você receberá o laudo no e-mail/WhatsApp.");
        setEmail("");
        setWhatsapp("");
        setFileAnalyze(null);
      } else {
        toast.error(data.error || data.message || "Erro ao enviar solicitação.");
      }
    } catch (error) {
      toast.error("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#020617] text-slate-200 selection:bg-blue-500/30 font-sans">

      {/* --- CYBER BACKGROUND ELEMENTS --- */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-20">
        {/* Futurist Grid */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

        {/* Neural Glows */}
        <div className="absolute -top-[10%] -right-[10%] w-[70%] h-[70%] bg-blue-600/20 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute top-[20%] -left-[10%] w-[50%] h-[50%] bg-indigo-600/15 rounded-full blur-[120px] animate-pulse-slow delay-700"></div>
      </div>

      {/* --- NAV BAR (GLASS) --- */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#020617]/40 backdrop-blur-xl px-10 py-5 flex justify-between items-center transition-all">
        <div className="flex items-center gap-3 cursor-pointer group">
          <ValidusLogo className="w-12 h-12 group-hover:scale-110 transition-transform drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]" />
          <span className="text-2xl font-black tracking-tighter text-white">VALIDUS <span className="text-blue-500">IA</span></span>
        </div>

        <div className="hidden lg:flex gap-10 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
          <a href="#features" className="hover:text-blue-400 transition-colors">Tecnologia</a>
          <a href="#compare" className="hover:text-blue-400 transition-colors">Benchmark</a>
          <a href="#security" className="hover:text-blue-400 transition-colors">Segurança</a>
          <a href="#planos" className="hover:text-blue-400 transition-colors">Planos</a>
        </div>

        <div className="flex items-center gap-6">
          <Link href="/login">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-white transition-colors hidden sm:inline-block cursor-pointer">Login</span>
          </Link>
          <Link href="/login">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white font-black text-[10px] uppercase tracking-[0.2em] px-8 h-12 rounded-xl shadow-[0_0_30px_rgba(37,99,235,0.3)] transition-all hover:scale-105 active:scale-95">
              Acesso Premium
            </Button>
          </Link>
        </div>
      </nav>

      {/* --- HERO SECTION (CYBER IMPACT) --- */}
      <section className="relative pt-48 pb-32 px-10 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-xl border border-blue-500/20 bg-blue-500/5 text-blue-400 text-[10px] font-black uppercase tracking-[0.3em] mb-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <Sparkles size={14} className="animate-pulse" /> Engine Cognitiva v4 Ativa
          </div>

          <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-10 text-white leading-[0.9] animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
            PARE DE LER.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-white italic">
              COMECE A VENCER.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-400 max-w-4xl mx-auto mb-16 leading-relaxed font-medium animate-in fade-in slide-in-from-bottom-12 duration-1000 delay-300">
            A primeira inteligência artificial verticalizada para <strong className="text-white">Engenharia de Licitações</strong>. Auditoria de editais, análise de riscos e gestão de certidões em um único dashboard Cyber-Glass.
          </p>

          <form onSubmit={handleCaptureLead} className="flex flex-col gap-6 justify-center items-center mb-24 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-16 duration-1000 delay-500 bg-slate-900/40 p-8 rounded-3xl border border-white/5 backdrop-blur-md shadow-2xl">
            <div className="w-full text-center mb-2">
              <p className="text-white font-black text-xl tracking-tighter">Faça sua 1ª análise de edital gratuitamente</p>
              <p className="text-slate-400 text-sm font-medium mt-1">A IA processará o edital e enviaremos o laudo corporativo para o seu e-mail.</p>
            </div>
            
            <div className="flex flex-col gap-4 w-full">
              <div className="relative group/upload h-20 w-full">
                <Input 
                  type="file" 
                  accept=".pdf" 
                  onChange={(e) => setFileAnalyze(e.target.files?.[0] || null)} 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" 
                  required
                />
                <div className="h-full border border-dashed border-white/10 rounded-2xl flex flex-row items-center justify-center gap-4 text-center px-4 transition-all group-hover/upload:border-blue-500/50 group-hover/upload:bg-blue-600/5 bg-[#0B1121]">
                  <div className="h-12 w-12 shrink-0 rounded-xl bg-blue-600/10 flex items-center justify-center shadow-inner group-hover/upload:scale-110 transition-transform">
                    {fileAnalyze ? <FileText className="h-6 w-6 text-emerald-500" /> : <Upload className="h-6 w-6 text-blue-500" />}
                  </div>
                  <div className="text-left overflow-hidden">
                    <p className="text-base font-bold text-white truncate max-w-[200px]">{fileAnalyze ? fileAnalyze.name : "Anexar Arquivo do Edital"}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-widest font-black">{fileAnalyze ? (fileAnalyze.size / 1024 / 1024).toFixed(1) + " MB" : "Apenas arquivos PDF"}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="relative flex-1 group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                  <Input
                    type="email"
                    placeholder="Seu e-mail profissional"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="relative h-16 px-6 text-sm bg-[#0B1121] border-white/5 text-white placeholder:text-slate-600 focus-visible:ring-blue-500 rounded-2xl font-bold w-full"
                    required
                  />
                </div>
                <div className="relative flex-1 group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                  <Input
                    type="tel"
                    placeholder="Seu WhatsApp (com DDD)"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="relative h-16 px-6 text-sm bg-[#0B1121] border-white/5 text-white placeholder:text-slate-600 focus-visible:ring-emerald-500 rounded-2xl font-bold w-full"
                    required
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="h-16 w-full bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl shadow-[0_0_30px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? <Loader2 className="mr-2 w-5 h-5 animate-spin" /> : "Analisar Meu Edital Agora"}
              {!loading && <ArrowRight className="ml-3 w-5 h-5" />}
            </Button>
          </form>

          {/* DASHBOARD PREVIEW (MOCKUP PREMIUM) */}
          <div className="relative mx-auto max-w-6xl rounded-[3rem] border border-white/10 bg-[#0B1121] shadow-[0_0_100px_rgba(0,0,0,0.5)] overflow-hidden group animate-in zoom-in-95 duration-1000 delay-700">
            <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent z-10 opacity-60"></div>
            <div className="absolute inset-0 bg-blue-600/5 group-hover:bg-blue-600/0 transition-all duration-1000"></div>

            <Image
              src="/dashboard-preview.png"
              alt="Validus v3 Cyber-Glass"
              width={1400}
              height={900}
              className="w-full h-auto opacity-90 group-hover:opacity-100 transition-all duration-1000 group-hover:scale-[1.02]"
            />

            {/* Floating Tech Badges */}
            <div className="absolute top-10 left-10 z-20 flex flex-col gap-4">
              <div className="glass-card bg-blue-600/10 border-blue-500/20 px-4 py-2 rounded-xl backdrop-blur-md flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-[10px] font-black text-white uppercase tracking-widest">Neural Score Ativo</span>
              </div>
            </div>

            <div className="absolute bottom-12 right-12 z-20 bg-slate-950/80 backdrop-blur-2xl border border-white/5 p-6 rounded-3xl shadow-2xl flex items-center gap-5 translate-y-4 group-hover:translate-y-0 transition-transform duration-700">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-700 flex items-center justify-center shadow-lg">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mb-1">Status Global</p>
                <p className="text-xl font-black text-white tracking-tighter italic">V3.0.4 ENTERPRISE</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- TRUST BAR --- */}
      <section className="py-20 border-y border-white/5 bg-white/[0.01] relative overflow-hidden">
        <div className="max-w-full mx-auto text-center">
          <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.4em] mb-12">
            Protocolos de Integração Homologados
          </p>

          <div className="relative w-full flex overflow-hidden">
            <div className="animate-infinite-scroll flex gap-24 items-center px-10">
              {[...Array(2)].map((_, i) => (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-2 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer shrink-0">
                    <span className="font-black text-3xl text-white tracking-tighter">gov.br</span>
                  </div>
                  <div className="flex items-center gap-3 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer shrink-0">
                    <span className="font-bold text-2xl text-slate-200 tracking-tight">PNCP</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer shrink-0">
                    <span className="font-bold text-2xl text-blue-400">Compras</span>
                    <span className="font-light text-2xl text-white">net</span>
                  </div>
                  <div className="flex items-center gap-3 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer shrink-0">
                    <span className="font-black text-2xl text-yellow-500 italic">BB</span>
                    <span className="font-bold text-xl text-slate-300">Licitações</span>
                  </div>
                  <div className="flex items-center gap-2 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer shrink-0">
                    <span className="font-black text-2xl text-blue-500">CAIXA</span>
                  </div>
                  <div className="w-1 h-1 bg-white/10 rounded-full"></div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURES GRID (GLASS CARDS) --- */}
      <section id="features" className="py-32 px-10 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-24">
            <h2 className="text-4xl md:text-6xl font-black mb-8 text-white tracking-tighter">Tecnologia Cognitiva de Ponta</h2>
            <p className="text-slate-500 text-xl font-medium leading-relaxed">
              Desenvolvemos a stack mais avançada do mercado para quem não pode se dar ao luxo de errar.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 glass-card rounded-[2.5rem] border-white/5 p-12 hover:border-blue-500/30 transition-all group overflow-hidden relative">
              <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 transition-opacity">
                <Search size={200} className="text-blue-500" />
              </div>
              <div className="w-16 h-16 bg-blue-600/10 rounded-2xl flex items-center justify-center mb-8 text-blue-500 border border-blue-500/20 group-hover:scale-110 transition-transform">
                <Search size={32} />
              </div>
              <h3 className="text-3xl font-black text-white mb-6 tracking-tighter italic">Deep Scanning IA</h3>
              <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
                Não é apenas texto. Nossa IA mapeia o DNA do edital, identificando áreas de risco, exigências técnicas ocultas e prazos críticos em segundos, mesmo em arquivos escaneados de baixa qualidade.
              </p>
            </div>

            <div className="glass-card rounded-[2.5rem] border-white/5 p-12 hover:border-indigo-500/30 transition-all group">
              <div className="w-16 h-16 bg-indigo-600/10 rounded-2xl flex items-center justify-center mb-8 text-indigo-500 border border-indigo-500/20 group-hover:scale-110 transition-transform">
                <Scale size={32} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 tracking-tighter italic">Risk Guard</h3>
              <p className="text-slate-500 font-medium leading-relaxed">
                Auditoria automática baseada na Lei 14.133. Identificamos cláusulas restritivas que sua concorrência nem percebeu.
              </p>
            </div>

            <div className="glass-card rounded-[2.5rem] border-white/5 p-12 hover:border-emerald-500/30 transition-all group">
              <div className="w-16 h-16 bg-emerald-600/10 rounded-2xl flex items-center justify-center mb-8 text-emerald-500 border border-emerald-500/20 group-hover:scale-110 transition-transform">
                <BarChart3 size={32} />
              </div>
              <h3 className="text-2xl font-black text-white mb-4 tracking-tighter italic">Neural Match</h3>
              <p className="text-slate-500 font-medium leading-relaxed">
                Cruzamento inteligente entre seu Acervo Técnico e o Edital. Saiba se você é habilitado antes de começar a trabalhar.
              </p>
            </div>

            <div className="md:col-span-2 glass-card rounded-[2.5rem] border-white/5 p-12 hover:border-blue-500/30 transition-all group relative overflow-hidden">
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-600/10 blur-[100px] -z-10"></div>
              <div className="w-16 h-16 bg-blue-600/10 rounded-2xl flex items-center justify-center mb-8 text-blue-500 border border-blue-500/20 group-hover:scale-110 transition-transform">
                <Zap size={32} />
              </div>
              <h3 className="text-3xl font-black text-white mb-6 tracking-tighter italic">Automação de Vault</h3>
              <p className="text-slate-400 text-lg leading-relaxed">
                Seu "Cofre Digital" nunca foi tão inteligente. Organizamos, classificamos e monitoramos a validade de toda a sua documentação técnica e jurídica 24/7.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- PRICING (CYBER CARDS) --- */}
      <section id="planos" className="py-32 px-10 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-black mb-8 text-white tracking-tighter">Planos Enterprise</h2>
          <p className="text-slate-500 mb-24 max-w-3xl mx-auto text-xl font-medium">
            Escolha o nível de inteligência que sua empresa precisa para escalar.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            {PLANS.map((plan: any) => (
              <div key={plan.id} className={`relative p-12 rounded-[3rem] border flex flex-col transition-all duration-700 ${plan.popular ? 'border-blue-500 bg-blue-600/5 shadow-[0_0_50px_rgba(37,99,235,0.2)] scale-105 z-10' : 'border-white/5 bg-slate-900/40 hover:bg-slate-900/60'}`}>
                {plan.popular && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-black px-6 py-2 rounded-xl shadow-lg uppercase tracking-widest">
                    Mais Utilizado
                  </div>
                )}
                <div className="mb-12 text-left">
                  <h3 className="text-2xl font-black text-white mb-3 italic tracking-tighter">{plan.name}</h3>
                  <p className="text-sm text-slate-500 font-bold leading-relaxed">{plan.description}</p>
                </div>
                <div className="text-5xl font-black text-white mb-12 text-left tracking-tighter italic">
                  {plan.price !== "Customizado" && <span className="text-xl font-normal text-slate-500">R$ </span>}
                  {plan.price}
                  {plan.price !== "Customizado" && <span className="text-sm text-slate-600 font-black uppercase tracking-widest ml-2">/mês</span>}
                </div>
                <ul className="space-y-6 mb-12 flex-1 text-left">
                  {plan.features.map((feature: string, i: number) => (
                    <li key={i} className="flex items-start gap-4 text-sm text-slate-400 font-medium">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link href="/login" className="mt-auto">
                  <Button className={`w-full h-16 rounded-[1.5rem] font-black text-[10px] uppercase tracking-[0.2em] transition-all ${plan.popular ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_30px_rgba(37,99,235,0.3)]' : 'bg-white/5 hover:bg-white/10 text-white'}`}>
                    Iniciar Sessão {plan.name}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FOOTER (CYBER STYLE) --- */}
      <footer className="py-20 border-t border-white/5 bg-[#020617] px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-10 text-center md:text-left">
          <div className="space-y-4">
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <ValidusLogo className="w-8 h-8" />
              <span className="text-xl font-black text-white tracking-tighter">VALIDUS IA</span>
            </div>
            <p className="text-slate-600 text-xs font-bold uppercase tracking-widest">
              © 2026 Validus Cognitive Tech.<br />
              All systems online.
            </p>
          </div>
          <div className="flex gap-10 text-[10px] font-black text-slate-600 uppercase tracking-widest">
            <a href="#" className="hover:text-blue-500 transition-colors">Termos</a>
            <a href="#" className="hover:text-blue-500 transition-colors">Privacidade</a>
            <a href="#" className="hover:text-blue-500 transition-colors">Infra</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
